// The rolling 6-month view of Capability and Activity, from Ronan's Capability
// mode design (2026-09-28: "bring the Whoop style graph back"). One point
// per week, Monday to Sunday, the last 26 weeks up to this one, so the current
// month is always on the axis. Capability comes from the backend, worked out
// again week by week from what people had done (GET …/capability/trend), with
// the Assessments that ran marked on the line.

import type { TrendView } from '../../../../../../api/capability';

export interface TrendSeries {
  key: 'capability' | 'activity';
  label: string;
  /** One value per week (0-100), or null before there is anything to show. */
  points: Array<number | null>;
}

export interface TrendWindow {
  weeks: Date[];
  /** Month label and the week index it starts at. */
  months: { label: string; index: number }[];
  /** Weeks an Assessment ran, marked on the chart. */
  assessments: { index: number; label: string }[];
}

const WEEKS = 26;
const DAY = 24 * 60 * 60 * 1000;

const monday = (d: Date) => {
  const day = (d.getUTCDay() + 6) % 7;
  return new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate() - day));
};

/** A week's key, as the backend sends it: its Monday, "YYYY-MM-DD" (UTC). */
export const weekKey = (week: Date) => week.toISOString().slice(0, 10);

export const trendWindow = (): TrendWindow => {
  const end = monday(new Date());
  const weeks = Array.from({ length: WEEKS }, (_, i) => new Date(end.getTime() - (WEEKS - 1 - i) * 7 * DAY));
  const months: TrendWindow['months'] = [];
  weeks.forEach((w, i) => {
    const label = w.toLocaleString('en-US', { month: 'short', timeZone: 'UTC' });
    if (!months.length || months[months.length - 1].label !== label) months.push({ label, index: i });
  });
  return { weeks, months, assessments: [] };
};

/** A program marked on the line: the week it launched (went to Invited). */
export interface TrendEvent {
  index: number;
  kind: 'assessment' | 'training';
  title: string;
  /** When it became Invited. */
  launchedOn: Date;
  /** Whole percent of invited learners who have completed it. */
  completionRate: number;
  /** The capabilities it assesses. */
  capabilities: string[];
}

/**
 * The backend's trend as the chart draws it: the Capability line on this
 * window's weeks (null where nothing was assessed yet), and the Assessments
 * that ran, each on its week.
 */
export const trendFromApi = (
  view: TrendView | null | undefined
): { window: TrendWindow; series: TrendSeries[]; events: TrendEvent[] } => {
  const window = trendWindow();
  const index = new Map(window.weeks.map((w, i) => [weekKey(w), i]));
  const byWeek = new Map((view?.weeks || []).map((w) => [w.weekStart, w.percent]));
  const points = window.weeks.map((w) => byWeek.get(weekKey(w)) ?? null);
  const events: TrendEvent[] = (view?.events || [])
    .map((e) => ({
      index: index.get(e.weekStart) ?? -1,
      kind: e.kind,
      title: e.title,
      launchedOn: new Date(e.launchedOn),
      completionRate: e.completionRate,
      capabilities: e.capabilities
    }))
    .filter((e) => e.index >= 0);
  window.assessments = events
    .filter((e) => e.kind === 'assessment')
    .map((e) => ({ index: e.index, label: 'Assessment' }));
  return { window, series: [{ key: 'capability', label: 'Capability', points }], events };
};

/** A line needs two points: with fewer there is no trend to draw yet. */
export const hasTrend = (points: Array<number | null>): boolean => points.filter((p) => p !== null).length >= 2;

/**
 * The Activity tier as a weekly line: the saved weekly snapshots, each held
 * until the next, ending on the current %. Weeks before the first snapshot
 * have no point.
 */
export const tierHistoryPoints = (
  window: TrendWindow,
  history: { weekStart: string; score: number }[],
  current: number | null
): Array<number | null> => {
  const saved = new Map(history.map((p) => [p.weekStart, p.score]));
  const last = window.weeks.length - 1;
  let held: number | null = null;
  return window.weeks.map((week, i) => {
    const score = saved.get(weekKey(week));
    if (score !== undefined) held = Math.round(score);
    return i === last && current !== null ? current : held;
  });
};
